<template>
  <q-page class="home-page">
    <!-- Welcome Section (mobile only) -->
    <WelcomeMsg
      v-if="isMobile"
      :first-name="user.first_name"
      :title-class="isMobile ? 'q-mb-none' : 'q-mb-xs'"
      :wrapper-class="'welcome-section ' + (isMobile ? 'px-mobile' : 'px-desktop')"
    />

    <!-- Map -->
    <div class="map-container">
      <Map
        ref="mapComponent"
        v-model:layerId="selectedLayer"
        @expand="onMapExpand"
        @close="onMapClose"
        @layer-changed="onLayerChange"
      />
    </div>

    <!-- Query Slider -->
    <div class="query-slider-container" :class="isMobile ? 'px-mobile' : 'px-desktop'">
      <div class="query-slider">
        <q-btn
          v-for="query in farmQueries"
          :key="query.id"
          color="primary"
          :text-color="selectedQueryId === query.id ? 'grey-2' : 'white'"
          :label="$t(query.title)"
          :class="isMobile ? 'query-btn query-btn--mobile' : 'query-btn'"
          @click="selectQuery(query.id)"
        />
      </div>
    </div>

    <!-- Feed -->
    <div class="feed-container" :class="isMobile ? 'q-px-md' : 'px-desktop'">
      <SocialFeedView :show-preview="true" />
    </div>
  </q-page>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import Map from '@/common/components/maps/simple-map.vue'
import SocialFeedView from './social-feed-view.vue'
import WelcomeMsg from '../layout/welcome-msg.vue'

const props = defineProps({
  user: { type: Object, default: () => ({ first_name: 'Guest' }) },
  showDesktopLayout: { type: Boolean, default: false }
})

const $q = useQuasar()
const { t } = useI18n()
const { proxy } = getCurrentInstance()

// Responsive
const isMobile = computed(() => $q.screen.lt.md)

// Refs
const mapComponent = ref(null)

// State
const selectedQueryId = ref(null)
const selectedLayer = ref(52)
const farmQueries = ref([])

// Lifecycle
onMounted(() => {
  fetchQueries()
})

// Methods
async function fetchQueries() {
  try {
    const response = await proxy.get('User/GetFarmQueries')

    farmQueries.value = response?.map(item => ({
      id: item.id,
      title: item.title,
      query: item.query,
      layerId: item.shdc_id
    })) || []

    if (farmQueries.value.length > 0) {
      selectedQueryId.value = farmQueries.value[0].id
    }
  } catch (error) {
    console.error('Failed to fetch farm queries:', error)
  }
}

function selectQuery(queryId) {
  selectedQueryId.value = queryId

  const query = farmQueries.value.find(q => q.id === queryId)
  if (!query) return

  selectedLayer.value = query.layerId
  mapComponent.value?.queryHomeField(query)

  $q.notify({
    message: `${t('Submitting')} ${t(query.title)}`,
    color: 'positive',
    timeout: 1000
  })
}

function onMapExpand(data) {
  // Map expanded
}

function onMapClose() {
  // Map closed
}

function onLayerChange(layerId) {
  // Layer changed
}
</script>

<style lang="scss" scoped>
.home-page {
  height: 100%;
  display: flex;
  flex-direction: column;
}

// ===== Shared spacing =====
.px-mobile {
  padding: 0.5rem 1rem;
}

.px-desktop {
  padding: 0.5rem 1rem 0.25rem;
}

// ===== Welcome section =====
.welcome-section {
  background: linear-gradient(to bottom, $grey-2 0%, white 100%);

  body.body--dark & {
    background: linear-gradient(to bottom, $dark 0%, $dark-page 100%);
  }
}

// ===== Map =====
.map-container {
  width: 100%;
  flex: 1 1 12.5rem;
  max-height: 25rem;
  display: flex;
  flex-direction: column;
}

// ===== Query slider =====
.query-slider-container {
  display: grid;
  grid-template-columns: 1fr;
}

.query-slider {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0.1875rem; // za shadow inace je odrezan

  &::-webkit-scrollbar {
    display: none;
  }
}

.query-btn {
  height: 3.125rem;
  min-width: 7.5rem;
  flex-shrink: 0;
  border-radius: 1.5rem;
  font-weight: 500;
  text-transform: none;
  white-space: nowrap;

  body.body--dark & {
    &.bg-grey-4 {
      background: $grey-8 !important;
      color: $grey-3 !important;
    }
  }
}

.query-btn--mobile {
  height: 2.5rem;
  min-width: 6rem;
  font-size: 0.8rem;
  padding: 0 0.75rem;
}

// ===== Feed =====
.feed-container {
  flex: 5 1 12.5rem;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
</style>